import { useEffect, useState } from 'react';
import { api, ErroApi, type Pendencias as Dados } from '../../api';
import { Carregando, Erro, Tabela, Vazio, cnpjFormatado } from '../../componentes/ui';

/**
 * O que um sistema emissor (o GestorIA) mandou e espera um administrador:
 * escritório novo, contador pedindo acesso e empresa querendo trocar de
 * escritório. Nada disso dá acesso antes de alguém aprovar aqui — é dado
 * fiscal de terceiro.
 */
export function Pendencias() {
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);

  async function carregar() {
    try {
      setDados(await api.pendencias());
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar as pendências.');
    }
  }
  useEffect(() => { void carregar(); }, []);

  async function agir(fn: () => Promise<unknown>) {
    setErro('');
    setOcupado(true);
    try {
      await fn();
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui concluir.');
    } finally {
      setOcupado(false);
    }
  }

  const total = dados ? dados.escritorios.length + dados.convites.length + dados.trocas.length : 0;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Pendências</h1>
          <p>
            O que o sistema da loja enviou e espera você: contador pedindo acesso, escritório novo e
            empresa querendo trocar de contador. Nada disso libera documento antes de aprovado.
          </p>
        </div>
      </header>

      <Erro>{erro}</Erro>

      {!dados ? <Carregando /> : total === 0 ? (
        <Vazio titulo="Nada pendente" descricao="Quando uma loja informar um contador novo ou trocar de escritório, aparece aqui." />
      ) : (
        <>
          {dados.convites.length > 0 && (
            <section>
              <h2>Contadores pedindo acesso</h2>
              <p className="nota">
                Aprovar cria a pessoa pelo celular (ou usa a que já existe) e a vincula ao escritório como
                contador: ela passa a ver as empresas desse escritório.
              </p>
              <Tabela
                itens={dados.convites}
                chaveDe={(c) => c.id}
                colunas={[
                  { rotulo: 'Contador', principal: true, celula: (c) => <strong>{c.nome}</strong> },
                  { rotulo: 'Celular', mono: true, celula: (c) => c.celular },
                  { rotulo: 'Escritório', celula: (c) => `${c.escritorio} · ${cnpjFormatado(c.escritorioCnpj)}` },
                  { rotulo: 'Empresa', fraco: true, celula: (c) => c.empresa ?? '—' },
                  { rotulo: 'CRC', fraco: true, mono: true, celula: (c) => c.crc ?? '—' },
                  {
                    rotulo: 'Ações', acoes: true,
                    celula: (c) => (
                      <>
                        <button className="btn btn-primary btn-sm" disabled={ocupado}
                          onClick={() => agir(() => api.resolverConvite(c.id, 'aprovar'))}>Aprovar</button>
                        <button className="link-botao" disabled={ocupado}
                          onClick={() => agir(() => api.resolverConvite(c.id, 'recusar'))}>Recusar</button>
                      </>
                    ),
                  },
                ]}
              />
            </section>
          )}

          {dados.trocas.length > 0 && (
            <section>
              <h2>Empresas trocando de escritório</h2>
              <p className="nota nota-atencao">
                Aprovar entrega todo o histórico da empresa ao escritório novo. Confira com o cliente antes.
              </p>
              <Tabela
                itens={dados.trocas}
                chaveDe={(t) => t.id}
                colunas={[
                  { rotulo: 'Empresa', principal: true, celula: (t) => <strong>{t.empresa}</strong> },
                  { rotulo: 'CNPJ', fraco: true, mono: true, celula: (t) => cnpjFormatado(t.empresaCnpj) },
                  { rotulo: 'Hoje', celula: (t) => t.escritorioAtual },
                  { rotulo: 'Pedido', celula: (t) => t.escritorioNovo },
                  {
                    rotulo: 'Ações', acoes: true,
                    celula: (t) => (
                      <>
                        <button className="btn btn-primary btn-sm" disabled={ocupado}
                          onClick={() => agir(() => api.resolverTroca(t.id, 'aprovar'))}>Trocar</button>
                        <button className="link-botao" disabled={ocupado}
                          onClick={() => agir(() => api.resolverTroca(t.id, 'recusar'))}>Manter</button>
                      </>
                    ),
                  },
                ]}
              />
            </section>
          )}

          {dados.escritorios.length > 0 && (
            <section>
              <h2>Escritórios novos</h2>
              <p className="nota">
                Nasceram do cadastro de contador feito na loja. Confirmar só tira o aviso: acesso continua
                vindo da pessoa aprovada acima.
              </p>
              <Tabela
                itens={dados.escritorios}
                chaveDe={(e) => e.id}
                colunas={[
                  { rotulo: 'Escritório', principal: true, celula: (e) => <strong>{e.nome}</strong> },
                  { rotulo: 'CNPJ', fraco: true, mono: true, celula: (e) => cnpjFormatado(e.cnpj) },
                  { rotulo: 'Empresas', numero: true, celula: (e) => e.empresas },
                  {
                    rotulo: 'Ações', acoes: true,
                    celula: (e) => (
                      <button className="link-botao" disabled={ocupado}
                        onClick={() => agir(() => api.confirmarEscritorio(e.id))}>Confirmar</button>
                    ),
                  },
                ]}
              />
            </section>
          )}
        </>
      )}
    </div>
  );
}
